/**
 * The two cells every comparison row has whichever layout draws it: the item (what the customer
 * asked for, our own stock, suppliers dropped as unrankable) and the allocation summary (what is
 * picked, what it costs, auto-cover). Shared by the matrix and the ranked view so a change to
 * either reaches both — lifted verbatim out of `WorkbenchTable` on 2026-11-03.
 */

import { Badge } from '../../components/Badge'
import { Money } from '../../components/Money'
import { formatMoney } from '../../lib/format'
import { summariseItem, type NormalisedItem } from '../../lib/compare'
import type { AllocationRow, Currency } from '../../lib/types'
import type { DisplayCurrency } from '../../lib/displayCurrency'

export interface InHouseOffer {
  price: { amount: number; currency: string; uom: string; buying: boolean } | null
  available?: number
}

export function ItemCell({
  item,
  supplierName,
  onPriceLine,
  onUseOurStock,
  inHouseFor,
}: {
  item: NormalisedItem
  supplierName: Map<string, string>
  onPriceLine?: (enquiryRowNo: string) => void
  onUseOurStock?: (rowNo: string) => void
  inHouseFor?: (enquiryRowNo: string) => InHouseOffer
}) {
  return (
    <td className="sticky">
      <div style={{ fontWeight: 700, whiteSpace: 'normal' }}>{item.name}</div>
      <div style={{ color: 'var(--muted)', fontSize: 11.5 }}>
        {item.item_code}
        {onPriceLine ? (
          <>
            {' · '}
            {/* The alternatives to the fresh offers, in one dialog (Akhil, 2026-09-15). */}
            <span
              className="linkish"
              onClick={() => onPriceLine(item.enquiry_row_no)}
              title="Price this line from our own stock, a contract, or a past supplier price — instead of one of the offers here"
            >
              price this line
            </span>
          </>
        ) : null}
      </div>

      {/* Our own stock, as a tag — the option the offers are competing with. */}
      {(() => {
        const own = inHouseFor?.(item.enquiry_row_no)
        if (!own || !own.price) return null
        const stock = own.available
        return (
          <div style={{ marginTop: 3 }}>
            <span
              className="pill"
              role={onUseOurStock ? 'button' : undefined}
              tabIndex={onUseOurStock ? 0 : undefined}
              style={{ background: 'var(--teal)', color: '#fff', cursor: onUseOurStock || onPriceLine ? 'pointer' : undefined }}
              title={`${own.price.buying ? 'In-house cost' : 'Our SELLING price, not a cost'} per ${own.price.uom}${
                stock !== undefined ? ` · ${stock} ${item.uom} free to commit` : ''
              }. Click to price this line from our stock at this rate — it leaves the comparison and goes on the quotation.`}
              // One click prices it (2026-09-18); the dialog stays reachable via "price this line".
              onClick={() => (onUseOurStock ?? onPriceLine)?.(item.enquiry_row_no)}
              onKeyDown={(e) => {
                if (onUseOurStock && (e.key === 'Enter' || e.key === ' ')) {
                  e.preventDefault()
                  onUseOurStock(item.enquiry_row_no)
                }
              }}
            >
              our stock {formatMoney({ amount: own.price.amount, currency: own.price.currency as Currency })}/{own.price.uom}
              {stock !== undefined ? ` · ${stock} on hand` : ''}
            </span>
          </div>
        )
      })()}

      {/* What the customer actually wrote. The item name is our vocabulary; this
          is theirs, and it is what a doubtful allocation gets checked against. */}
      {item.lines.map((line) => (
        <div
          key={line.line_id}
          style={{ marginTop: 3, whiteSpace: 'pre-line', fontSize: 11.5, color: 'var(--muted)' }}
        >
          <span className="pill" title="Customer's own serial number">
            #{line.cust_sno}
          </span>{' '}
          {line.description}
          {line.edited ? (
            <div style={{ color: 'var(--ink)', fontSize: 11.5, whiteSpace: 'pre-line' }} title="The description as edited on the RFQ builder — prints on the quotation">
              ✎ {line.edited}
            </div>
          ) : null}
        </div>
      ))}

      {/* A supplier dropped for an unconvertible UOM or currency must be visible, not silently gone. */}
      {item.excluded.map((excluded) => (
        <div key={excluded.supplier} style={{ marginTop: 3, whiteSpace: 'normal' }}>
          <Badge tone="danger" title={excluded.reason}>
            {supplierName.get(excluded.supplier) ?? excluded.supplier} excluded
          </Badge>
          <div style={{ color: 'var(--muted)', fontSize: 10.5 }}>
            {/* Their price is still shown — withholding it just sends the
                purchaser back to the supplier screen to look it up. */}
            quoted <Money value={excluded.raw.rate} />/{excluded.raw.sq_uom} · {excluded.reason}
          </div>
        </div>
      ))}
    </td>
  )
}

export function AllocationCell({
  item,
  allocation,
  supplierName,
  baseCurrency,
  display,
  onAutoCover,
}: {
  item: NormalisedItem
  /** This row's allocation rows only — `rowsForItem`. */
  allocation: AllocationRow[]
  supplierName: Map<string, string>
  baseCurrency: Currency
  display?: DisplayCurrency
  onAutoCover: (itemCode: string, enquiryRowNo: string) => void
}) {
  const summary = summariseItem(item, allocation)
  return (
    <td className="sum">
      <div className="allocsum">
        <div className="lab">
          {summary.allocated}/{item.qty}{' '}
          {summary.remaining === 0 ? (
            <Badge tone="ok">covered</Badge>
          ) : summary.remaining > 0 ? (
            <Badge tone="warn">{summary.remaining} left</Badge>
          ) : (
            <Badge tone="danger">{-summary.remaining} over</Badge>
          )}
          {summary.supplierCount > 1 ? (
            <>
              {' · '}
              <Badge tone="violet">{summary.supplierCount} suppliers</Badge>
            </>
          ) : null}
        </div>
        {/*
          The chosen supplier(s) and what they SAID, here in the pinned column
          (Akhil, 2026-09-15): on a hundred-line enquiry the offer's remark sits in a
          cell somewhere off to the left, and scrolling out to read "subject to
          availability" on every line is the cost this saves. Only allocated offers —
          the decision, not the whole matrix.
        */}
        {allocation
          .filter((row) => row.allocated_qty > 0)
          .map((row) => {
            const offer = item.bySupplier.get(row.supplier)
            return (
              <div key={row.supplier} style={{ fontSize: 10.5, whiteSpace: 'normal', marginTop: 2 }}>
                <span style={{ fontWeight: 700 }}>{supplierName.get(row.supplier) ?? row.supplier}</span>
                {allocation.length > 1 ? ` · ${row.allocated_qty}` : ''}
                {offer?.raw.remark ? (
                  <span style={{ color: 'var(--teal)', fontStyle: 'italic' }}> “{offer.raw.remark}”</span>
                ) : null}
              </div>
            )
          })}
        <div className="lab">
          line <Money value={{ amount: summary.cost, currency: baseCurrency }} in={display?.currency} fx={display?.fx} /> ·{' '}
          <span
            style={{ cursor: 'pointer', color: 'var(--brand)', fontWeight: 700 }}
            onClick={() => onAutoCover(item.item_code, item.enquiry_row_no)}
          >
            auto-cover
          </span>
        </div>
      </div>
    </td>
  )
}
